import React from "react";

import { faSparkles, faTrashCan } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon, ButtonStandard, Icon, Skeleton } from "@performyard/py-components";

import type { RephraseBlockData } from "./types";

type InlineLoaderProps = {
	hasTrashIcon: boolean;
	isExpanded: boolean;
};

const InlineLoader = ({ hasTrashIcon = false, isExpanded = false }: InlineLoaderProps): React.ReactElement => {
	return (
		<div className="flex flex-col">
			<Skeleton className="h-4 w-4/12 rounded-none" />
			<div className="my-2 flex flex-col gap-1">
				<Skeleton className="h-4 w-12/12 rounded-none" />
				<Skeleton className="h-4 w-12/12 rounded-none" />
				<Skeleton className="h-4 w-8/12 rounded-none" />
				{isExpanded && (
					<div className="flex flex-col gap-1">
						<Skeleton className="h-4 w-12/12 rounded-none" />
						<Skeleton className="h-4 w-12/12 rounded-none" />
						<Skeleton className="h-4 w-8/12 rounded-none" />
					</div>
				)}
			</div>
			<div className="mt-2 flex gap-1">
				<Skeleton className="h-8 w-[70px] rounded-none" />
				<Skeleton className="h-8 w-[70px] rounded-none" />
				{hasTrashIcon && <Skeleton className="align-end ml-auto size-8 rounded-none" />}
			</div>
		</div>
	);
};

type RephraseBlockProps = {
	rephrasedData: RephraseBlockData;
	onInsert: (newAnswer: string) => void;
	onRetry: (retryText: string) => void;
	onDismiss: () => void;
	retryDisabled: boolean;
	rephraseOptionsCount: number;
	isExpanded: boolean;
};

export const RephraseBlock = ({
	rephrasedData,
	onInsert,
	onRetry,
	onDismiss,
	rephraseOptionsCount,
	retryDisabled = false,
	isExpanded = false,
}: RephraseBlockProps): React.ReactElement => {
	const handleOnInsert = (): void => onInsert(rephrasedData.data);
	const handleOnRetry = (): void => onRetry(rephrasedData.data);

	return (
		<div
			className="group"
			aria-busy={rephrasedData.inProgress}
			data-state={rephrasedData.inProgress ? "loading" : "ready"}
		>
			<div className="hidden group-data-[state=loading]:block">
				<InlineLoader hasTrashIcon={rephraseOptionsCount > 1} isExpanded={isExpanded} />
			</div>
			<div className="flex flex-col gap-2 py-2 group-data-[state=loading]:hidden">
				<div className="flex items-center gap-1 pb-1">
					<Icon icon={faSparkles} color="primary-5" size={16} />
					<span className="text-paragraph-3 font-semibold text-neutral-900">Rephrase:</span>
				</div>
				<div className="block">
					{rephrasedData.data ? (
						<p className="text-paragraph-3 font-normal whitespace-pre-wrap text-neutral-900">
							{rephrasedData.data}
						</p>
					) : (
						<p className="text-paragraph-3 font-normal whitespace-pre-wrap text-neutral-900">
							No response available.
						</p>
					)}
				</div>
			</div>

			<div className="mt-2 flex w-full items-center justify-between group-data-[state=loading]:hidden">
				<div className="flex gap-2">
					<ButtonStandard
						onClick={handleOnInsert}
						color="primary"
						disabled={rephrasedData.inProgress}
						data-testid="insert-rephrase-button"
					>
						Insert
					</ButtonStandard>
					<ButtonStandard
						onClick={handleOnRetry}
						variant="outline"
						disabled={retryDisabled || rephrasedData.inProgress}
						data-testid="retry-rephrase-button"
					>
						Retry
					</ButtonStandard>
				</div>
				{rephraseOptionsCount > 1 && (
					<ButtonIcon
						icon={faTrashCan}
						onClick={onDismiss}
						variant="outline"
						color="neutral"
						shade={5}
						size={24}
						dangerouslySetClassName="rounded border border-gray-300 p-2 transition hover:bg-gray-100"
						aria-label="Delete"
					/>
				)}
			</div>

			<hr className="mt-4 mb-2 h-px w-full border-none bg-neutral-2" />
		</div>
	);
};
